import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Relationships = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'relationships'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container relationships-page">
      <h1 className="page-title">Healthy Relationships</h1>

      {/* Include the image */}
      <img
        src="/images/wf-buildinghealthy.webp"
        alt="Healthy Relationships"
        className="page-image"
      />

      <p className="page-intro">Welcome to the Healthy Relationships section of Wholeness Forward.</p>

      <p className="page-text">
        Healthy relationships are essential for our mental and emotional well-being. Whether it's with a partner, family, friends, or colleagues,
        these connections provide support, joy, and a sense of belonging. In this section, we’ll explore the key elements of a healthy relationship,
        how to nurture positive connections, and why they are crucial for living a balanced and fulfilling life.
      </p>

      <p className="page-text">
        Healthy relationships are built on mutual respect, trust, and communication. They offer us a space for personal growth and emotional support,
        helping us navigate life's challenges with resilience. A strong relationship can enhance your sense of self-worth and well-being,
        while toxic relationships can detract from your overall health. It’s important to identify and cultivate the relationships that nurture you,
        and let go of the ones that harm you.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Key Elements of a Healthy Relationship</h2>
      <ul className="content-list">
        <li><strong>Communication:</strong> Open, honest communication builds trust and understanding between individuals.</li>
        <li><strong>Respect:</strong> Both parties acknowledge each other's boundaries and opinions.</li>
        <li><strong>Trust:</strong> Healthy relationships are built on trust, where both individuals feel safe and supported.</li>
        <li><strong>Empathy:</strong> Being able to understand and share the feelings of another is crucial for emotional connection.</li>
        <li><strong>Support:</strong> Offering emotional and practical support strengthens the bond and helps both individuals grow.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      <p className="page-text">Explore these videos to learn about the importance of healthy relationships and how to foster meaningful connections:</p>

      {loading ? (
        <p>Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  width="560"
                  height="315"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Join the Relationships Community</h3>
      <p className="page-text">
        Join the discussion and share your experiences, ask questions, and get advice on how to build stronger, more fulfilling relationships.
        Whether you're focused on romantic relationships, family, or friendships, this is the place to connect and learn from others.
      </p>
      <a href="/forum/Relationships" className="btn">Join the Forum</a>
    </div>
  );
};

export default Relationships;
